import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/useAuth';
import { useData } from '../hooks/useData';
import { apiFetch } from '../api';
import { LoadingSpinner } from '../components/Common';
import { DynamicIcon } from '../components/icons';
import { isPlatformScoped } from '../utils/permissions';
import { motion } from 'framer-motion';
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
    LineChart, Line
} from 'recharts';
import UserGuidePage from './UserGuidePage';

// --- Shared Components ---

const StatCard = ({ title, value, icon, color, delay, trend }) => {
    // trend can be:
    //   null          → no badge
    //   string        → plain label badge (used for breakdown text like "↓ In  ↑ Out")
    //   object        → { direction: 'up'|'down'|other, percentage: number }
    const isStringTrend = typeof trend === 'string';

    let trendColor = 'gray';
    let TrendIcon  = 'Minus';
    let trendText  = '-';

    if (!isStringTrend && trend) {
        if (trend.direction === 'up') {
            trendColor = 'green';
            TrendIcon  = 'ArrowUp';
            trendText  = `+${trend.percentage}%`;
        } else if (trend.direction === 'down') {
            trendColor = 'red';
            TrendIcon  = 'ArrowDown';
            trendText  = `-${trend.percentage}%`;
        } else {
            trendText  = '0%';
            TrendIcon  = 'Minus';
            trendColor = 'gray';
        }
    }

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: delay }}
            className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 hover:shadow-md transition-shadow"
        >
            <div className="flex items-center justify-between mb-4">
                <div className={`p-3 rounded-full bg-${color}-50 text-${color}-600 dark:bg-${color}-900/30 dark:text-${color}-400`}>
                    <DynamicIcon name={icon} className="h-6 w-6" />
                </div>
                {/* String trend → plain label; object trend → coloured badge; null → no badge */}
                {isStringTrend ? (
                    <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 whitespace-nowrap">
                        {trend}
                    </span>
                ) : trend ? (
                    <div className={`flex items-center gap-1 text-xs font-medium px-2.5 py-0.5 rounded-full bg-${trendColor}-100 text-${trendColor}-800 dark:bg-${trendColor}-900 dark:text-${trendColor}-300`}>
                        <DynamicIcon name={TrendIcon} className="h-3 w-3" />
                        <span>{trendText}</span>
                    </div>
                ) : null}
            </div>
            <div>
                <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">{title}</h3>
                <p className="text-3xl font-bold text-gray-900 dark:text-white mt-1">{value}</p>
            </div>
        </motion.div>
    );
};


const QuickActionCard = ({ title, icon, color, onClick, description, delay }) => (
    <motion.button
        onClick={onClick}
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.3, delay: delay }}
        className="flex flex-col items-center p-6 bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 hover:shadow-md hover:border-blue-200 dark:hover:border-blue-800 transition-all text-center group w-full"
    >
        <div className={`p-4 rounded-full bg-${color}-50 text-${color}-600 dark:bg-${color}-900/30 dark:text-${color}-400 mb-4 group-hover:scale-110 transition-transform`}>
            <DynamicIcon name={icon} className="h-8 w-8" />
        </div>
        <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">{title}</h4>
        <p className="text-sm text-gray-500 dark:text-gray-400">{description}</p>
    </motion.button>
);

const ChartSection = ({ title, children, delay, loading }) => (
    <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: delay }}
        className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 h-full"
    >
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">{title}</h3>
        <div className="h-[300px] w-full">
            {loading ? (
                <div className="h-full flex items-end gap-2 animate-pulse px-4">
                    {[55, 40, 75, 50, 65, 45, 60].map((h, i) => (
                        <div key={i} className="flex-1 flex gap-0.5 items-end" style={{ height: `${h}%` }}>
                            <div className="flex-1 bg-indigo-100 dark:bg-indigo-900/30 rounded-t" />
                            <div className="flex-1 bg-green-100 dark:bg-green-900/30 rounded-t" />
                        </div>
                    ))}
                </div>
            ) : children}
        </div>
    </motion.div>
);

const WelcomeBanner = ({ setActivePage, user, onOpenGuide, isPartnerAdmin }) => (
    <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-indigo-50 via-blue-50 to-white dark:from-gray-800 dark:via-gray-800 dark:to-gray-900 p-8 shadow-sm border border-indigo-100 dark:border-gray-700 mb-8"
    >
        <div className="relative z-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div>
                <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                    Welcome back, {user?.name || 'User'}!
                </h1>
                <p className="text-gray-600 dark:text-gray-300 mt-2 max-w-2xl text-lg">
                    {isPartnerAdmin
                        ? 'Manage your tenants, monitor usage, and oversee your partner operations.'
                        : 'Manage multi-tenancy, monitor system health, and configure global platform settings from one central hub.'
                    }
                </p>
                <div className="mt-6 flex flex-wrap gap-3">
                    <button
                        onClick={() => setActivePage && setActivePage('tenants')}
                        className="inline-flex items-center px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-lg transition-colors shadow-sm"
                    >
                        <DynamicIcon name="Plus" className="w-4 h-4 mr-2" />
                        Onboard Tenant
                    </button>
                    {!isPartnerAdmin && (
                        <button
                            onClick={() => setActivePage && setActivePage('settings')}
                            className="inline-flex items-center px-4 py-2 bg-white hover:bg-gray-50 text-gray-700 text-sm font-medium rounded-lg border border-gray-200 shadow-sm transition-colors dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                        >
                            <DynamicIcon name="Settings" className="w-4 h-4 mr-2" />
                            Global Config
                        </button>
                    )}
                    <button
                        onClick={() => window.open('/user-guide', '_blank')}
                        className="inline-flex items-center px-4 py-2 bg-white hover:bg-indigo-50 text-indigo-600 text-sm font-medium rounded-lg border border-indigo-200 shadow-sm transition-colors dark:bg-gray-800 dark:border-indigo-800 dark:text-indigo-400 dark:hover:bg-indigo-900/20"
                    >
                        <DynamicIcon name="BookOpen" className="w-4 h-4 mr-2" />
                        User Manual
                    </button>
                </div>
            </div>
            <div className="hidden md:block opacity-10 transform translate-x-4">
                <DynamicIcon name="ShieldCheck" className="w-32 h-32 text-indigo-600 dark:text-indigo-400" />
            </div>
        </div>
        <div className="absolute top-0 right-0 -mt-10 -mr-10 h-64 w-64 rounded-full bg-indigo-500/5 blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 left-0 -mb-10 -ml-10 h-64 w-64 rounded-full bg-blue-500/5 blur-3xl pointer-events-none" />
    </motion.div>
);

// Custom tooltip — shows the real calendar date and all 5 metric values
const ActivityTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
        <div className="bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-lg shadow-lg px-4 py-3 text-sm">
            <p className="font-semibold text-gray-700 dark:text-gray-200 mb-1">
                {payload[0]?.payload?.date || label}
            </p>
            {payload.map(p => (
                <div key={p.dataKey} className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full" style={{ background: p.stroke }} />
                    <span className="text-gray-500 dark:text-gray-400">{p.name}:</span>
                    <span className="font-medium text-gray-800 dark:text-gray-100">{p.value.toLocaleString()}</span>
                </div>
            ))}
        </div>
    );
};

// --- Main Page Component ---

const AdminDashboardPage = ({ setActivePage, user }) => {
    const { branding } = useAuth();
    const tenantPrimary = branding?.theme_config?.theme_color || branding?.theme_config?.primary_color || '#6366f1';
    
    const [showGuide, setShowGuide] = useState(false);
    const { data: tenants, isLoading: tenantsLoading } = useData('/tenants', null, true);
    const { data: partners, isLoading: partnersLoading } = useData('/partners', null, true);
    const { data: platformStats, isLoading: statsLoading } = useData('/insights/platform-stats', null, true);

    // Scoped storage — re-fetched whenever chartScope changes
    const [scopedStorageGb, setScopedStorageGb] = useState(null); // null = use platformStats total
    const [storageLoading, setStorageLoading] = useState(false);

    // Real 7-day daily activity data
    const [activityData, setActivityData]   = useState([]);
    const [activityLoading, setActivityLoading] = useState(true);
    const [tenantList, setTenantList]       = useState([]);
    const [chartScope, setChartScope]       = useState('all'); // 'all' | 'platform' | <tenant_id>

    useEffect(() => {
        setActivityLoading(true);
        setActivityData([]); // Reset immediately so Data Transfer clears on scope change
        const qs = chartScope === 'all' ? '?scope=all'
                 : chartScope === 'platform' ? '?scope=platform'
                 : `?tenant_id=${encodeURIComponent(chartScope)}`;

        apiFetch(`/insights/activity-chart${qs}`)
            .then(res => {
                const daily   = res?.daily   ?? (Array.isArray(res) ? res : []);
                const tenants = res?.tenants ?? [];
                setActivityData(daily);                             // Always update (even if empty)
                if (tenants.length > 0) setTenantList(tenants);    // Keep tenant list populated
            })
            .catch(err => console.error('[AdminDashboard] activity-chart failed', err))
            .finally(() => setActivityLoading(false));

        // Re-fetch storage scoped to the selected tenant
        setStorageLoading(true);
        const storageQs = chartScope !== 'all'
            ? `?tenant_id=${encodeURIComponent(chartScope)}`
            : '';
        apiFetch(`/insights/platform-stats${storageQs}`)
            .then(res => setScopedStorageGb(res?.storage_usage_gb?.value ?? null))
            .catch(() => setScopedStorageGb(null))
            .finally(() => setStorageLoading(false));
    }, [chartScope]);

    if (tenantsLoading || partnersLoading || statsLoading) {
        return (
            <div className="flex h-screen items-center justify-center">
                <LoadingSpinner />
            </div>
        );
    }

    const tenantCount = tenants ? tenants.filter(t => t.id !== 'platform').length : 0;
    const partnerCount = partners ? partners.length : 0;

    const formatBytes = (bytes, decimals = 2) => {
        if (!+bytes) return '0 Bytes';
        const k = 1024;
        const dm = decimals < 0 ? 0 : decimals;
        const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
    };

    const getVal = (key) => platformStats?.[key]?.value || 0;
    const getTrend = (key) => platformStats?.[key]?.trend || null;

    // ── Week totals (needed by both stats tiles and chart) ───────────────────
    const weekApps      = activityData.reduce((s, d) => s + (d.apps_run             || 0), 0);
    const weekWorkflows = activityData.reduce((s, d) => s + (d.workflows_run        || 0), 0);
    const weekAiml      = activityData.reduce((s, d) => s + (d.aiml_run             || 0), 0);
    const weekDash      = activityData.reduce((s, d) => s + (d.dashboards_accessed  || 0), 0);
    const weekDataIn    = activityData.reduce((s, d) => s + (d.data_in_bytes        || 0), 0);
    const weekDataOut   = activityData.reduce((s, d) => s + (d.data_out_bytes       || 0), 0);
    const hasActivity   = weekApps > 0 || weekDash > 0 || weekDataIn > 0 || weekDataOut > 0;
    
    const isPartnerAdmin = isPlatformScoped(user) && !!user?.partnerId;

    const stats = [
        { title: 'Active Tenants',    value: tenantCount,                icon: 'Users',      color: 'blue',   trend: null },
        !isPartnerAdmin && { title: 'Partners', value: partnerCount,     icon: 'Briefcase',  color: 'purple', trend: null },
        { title: 'Business Apps',     value: getVal('business_apps'),    icon: 'LayoutGrid', color: 'green',  trend: getTrend('business_apps') },
        { title: 'Contents',          value: getVal('contents'),         icon: 'PlayCircle', color: 'red',    trend: getTrend('contents') },
    ].filter(Boolean);

    // Scope label for display
    const scopeLabel = chartScope === 'all' ? 'All Tenants'
        : chartScope === 'platform' ? 'Platform'
        : (tenantList.find(t => t.id === chartScope)?.name || chartScope);

    // Convert bytes to MB for chart readability
    const chartData = activityData.map(d => ({
        ...d,
        data_in_mb:  d.data_in_bytes  ? +(d.data_in_bytes  / (1024 * 1024)).toFixed(3) : 0,
        data_out_mb: d.data_out_bytes ? +(d.data_out_bytes / (1024 * 1024)).toFixed(3) : 0,
    }));

    // Colour palette for the 7 trend lines
    const LINES = [
        { key: 'apps_run',             label: 'Apps Run',             color: tenantPrimary },
        { key: 'workflows_run',        label: 'Workflows Run',        color: '#22c55e' },
        { key: 'aiml_run',             label: 'AI / ML App Run',      color: '#f59e0b' },
        { key: 'dashboards_accessed',  label: 'Dashboards Accessed',  color: '#3b82f6' },
        { key: 'data_in_mb',           label: 'Data In (MB)',         color: '#14b8a6' },
        { key: 'data_out_mb',          label: 'Data Out (MB)',        color: '#f97316' },
    ];

    return (
        <>
        {showGuide && <UserGuidePage onClose={() => setShowGuide(false)} />}
        <div className="space-y-8 p-1">
            {/* Header */}
            <WelcomeBanner setActivePage={setActivePage} user={user} onOpenGuide={() => setShowGuide(true)} isPartnerAdmin={isPartnerAdmin} />

            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {stats.map((stat, index) => (
                    <StatCard key={stat.title} {...stat} delay={index * 0.1} />
                ))}
            </div>

            {/* ── Scope / Tenant filter bar ─────────────────────────────── */}
            <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.3 }}
                className="flex items-center gap-3 bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-xl px-5 py-3 shadow-sm"
            >
                <DynamicIcon name="Filter" className="w-4 h-4 text-gray-400" />
                <span className="text-xs font-medium text-gray-500 dark:text-gray-400 mr-1">View:</span>

                {/* All Tenants pill */}
                <button
                    onClick={() => setChartScope('all')}
                    className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                        chartScope === 'all'
                            ? 'bg-indigo-600 text-white shadow-sm'
                            : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                    }`}
                >
                    All Tenants
                </button>

                {/* Platform pill — hidden for Partner Admins */}
                {!isPartnerAdmin && (
                    <button
                        onClick={() => setChartScope('platform')}
                        className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                            chartScope === 'platform'
                                ? 'bg-indigo-600 text-white shadow-sm'
                                : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                        }`}
                    >
                        Platform
                    </button>
                )}

                {/* Per-tenant dropdown */}
                {tenantList.length > 0 && (
                    <select
                        value={!['all', 'platform'].includes(chartScope) ? chartScope : ''}
                        onChange={e => e.target.value && setChartScope(e.target.value)}
                        className="text-xs border border-gray-200 dark:border-gray-600 rounded-lg px-2 py-1 bg-white dark:bg-gray-700 text-gray-600 dark:text-gray-300 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                    >
                        <option value="">Select tenant…</option>
                        {tenantList.map(t => (
                            <option key={t.id} value={t.id}>{t.name}</option>
                        ))}
                    </select>
                )}

                {/* Current scope label */}
                <span className="ml-auto text-xs text-gray-400 dark:text-gray-500 italic">
                    Showing: <span className="font-medium text-indigo-500">{scopeLabel}</span>
                </span>
            </motion.div>

            {/* ── Charts + side tiles ───────────────────────────────────── */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Activity line chart — full 2/3 width */}
                <div className="lg:col-span-2">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: 0.4 }}
                        className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 h-full"
                    >
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">
                            System Activity
                            <span className="ml-2 text-xs font-normal text-gray-400 dark:text-gray-500">(Last 7 Days)</span>
                        </h3>

                        {/* Chart area */}
                        <div className="h-[300px] w-full">
                            {activityLoading ? (
                                <div className="h-full flex items-end gap-2 animate-pulse px-4">
                                    {[55, 40, 75, 50, 65, 45, 60].map((h, i) => (
                                        <div key={i} className="flex-1 flex gap-0.5 items-end" style={{ height: `${h}%` }}>
                                            <div className="flex-1 bg-indigo-100 dark:bg-indigo-900/30 rounded-t" />
                                            <div className="flex-1 bg-green-100 dark:bg-green-900/30 rounded-t" />
                                        </div>
                                    ))}
                                </div>
                            ) : !hasActivity ? (
                                <div className="h-full flex flex-col items-center justify-center text-gray-400 dark:text-gray-500 gap-2">
                                    <DynamicIcon name="BarChart2" className="w-10 h-10 opacity-30" />
                                    <p className="text-sm">No activity recorded in the last 7 days.</p>
                                </div>
                            ) : (
                                <ResponsiveContainer width="100%" height="100%">
                                    <LineChart data={chartData}>
                                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" />
                                        <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fill: '#6B7280' }} />
                                        <YAxis axisLine={false} tickLine={false} tick={{ fill: '#6B7280' }} allowDecimals={false} />
                                        <Tooltip content={<ActivityTooltip />} />
                                        <Legend />
                                        {LINES.map(l => (
                                            <Line
                                                key={l.key}
                                                type="monotone"
                                                dataKey={l.key}
                                                name={l.label}
                                                stroke={l.color}
                                                strokeWidth={2}
                                                dot={{ r: 3, fill: l.color }}
                                                activeDot={{ r: 5 }}
                                            />
                                        ))}
                                    </LineChart>
                                </ResponsiveContainer>
                            )}
                        </div>
                    </motion.div>
                </div>

                {/* Side tiles: S3 Storage + Data Transfer — react to chartScope */}
                <div className="lg:col-span-1 flex flex-col gap-6">
                    {/* S3 Storage tile */}
                    <motion.div
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.4, delay: 0.5 }}
                        className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 flex-1"
                    >
                        <div className="flex items-center justify-between mb-4">
                            <div className="p-3 rounded-full bg-pink-50 text-pink-600 dark:bg-pink-900/30 dark:text-pink-400">
                                <DynamicIcon name="Server" className="h-6 w-6" />
                            </div>
                            <span className="text-xs text-gray-400 italic">{scopeLabel}</span>
                        </div>
                        <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">S3 Storage Usage</h3>
                        <p className="text-3xl font-bold text-gray-900 dark:text-white mt-1">
                            {storageLoading ? (
                                <span className="text-lg text-gray-400 animate-pulse">Loading…</span>
                            ) : (
                                formatBytes(((scopedStorageGb !== null ? scopedStorageGb : getVal('storage_usage_gb')) || 0) * 1024 ** 3)
                            )}
                        </p>
                        <p className="text-xs text-gray-400 dark:text-gray-500 mt-2">Materialized data model storage</p>
                    </motion.div>

                    {/* Data Transfer tile */}
                    <motion.div
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.4, delay: 0.6 }}
                        className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 flex-1"
                    >
                        <div className="flex items-center justify-between mb-4">
                            <div className="p-3 rounded-full bg-teal-50 text-teal-600 dark:bg-teal-900/30 dark:text-teal-400">
                                <DynamicIcon name="ArrowLeftRight" className="h-6 w-6" />
                            </div>
                            <span className="text-xs bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 px-2 py-0.5 rounded-full whitespace-nowrap">
                                ↓ {formatBytes(weekDataIn)} &nbsp; ↑ {formatBytes(weekDataOut)}
                            </span>
                        </div>
                        <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Data Transfer (7d)</h3>
                        <p className="text-3xl font-bold text-gray-900 dark:text-white mt-1">
                            {formatBytes(weekDataIn + weekDataOut)}
                        </p>
                        <p className="text-xs text-gray-400 dark:text-gray-500 mt-2">Inbound + outbound this week</p>
                    </motion.div>
                </div>
            </div>

            {/* Week summary pills — only rendered metrics > 0 */}
            {!activityLoading && hasActivity && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex gap-3 flex-wrap">
                    {[
                        { label: 'Apps run',             value: weekApps,      icon: 'LayoutGrid',      color: 'text-indigo-500' },
                        { label: 'Workflows run',        value: weekWorkflows, icon: 'Workflow',        color: 'text-green-500'  },
                        { label: 'AI / ML runs',         value: weekAiml,      icon: 'BrainCircuit',    color: 'text-amber-500'  },
                        { label: 'Dashboards accessed',  value: weekDash,      icon: 'LayoutDashboard', color: 'text-blue-500'   },
                    ].filter(p => p.value > 0).map(p => (
                        <div key={p.label} className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-lg px-4 py-2 shadow-sm">
                            <DynamicIcon name={p.icon} className={`w-4 h-4 ${p.color}`} />
                            <span>
                                <span className="font-semibold text-gray-800 dark:text-gray-100">{p.value.toLocaleString()}</span> {p.label} this week
                            </span>
                        </div>
                    ))}
                </motion.div>
            )}

            {/* Quick Actions */}
            <div>
                <motion.h2
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.6 }}
                    className="text-xl font-semibold text-gray-900 dark:text-white mb-6"
                >
                    Quick Actions
                </motion.h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <QuickActionCard
                        title="Manage Tenants"
                        description={isPartnerAdmin ? 'Manage your tenants, subscriptions, and monitor usage.' : 'Onboard new tenants, manage subscriptions, and monitor usage.'}
                        icon="Users"
                        color="blue"
                        onClick={() => setActivePage('tenants')}
                        delay={0.7}
                    />
                    <QuickActionCard
                        title={isPartnerAdmin ? 'Manage Credits' : 'Manage Business Apps'}
                        description={isPartnerAdmin ? 'View your credit pool, allocate credits to tenants, and track usage.' : 'Configure app catalogs, deploy new versions, and manage access.'}
                        icon={isPartnerAdmin ? 'Wallet' : 'LayoutGrid'}
                        color="green"
                        onClick={() => setActivePage(isPartnerAdmin ? 'credits' : 'business_apps')}
                        delay={0.8}
                    />
                    {!isPartnerAdmin && (
                        <QuickActionCard
                            title="System Settings"
                            description="Configure platform-wide settings, security policies, and integrations."
                            icon="Settings"
                            color="gray"
                            onClick={() => setActivePage('settings')}
                            delay={0.9}
                        />
                    )}
                    {isPartnerAdmin && (
                        <QuickActionCard
                            title="Support Center"
                            description="View and manage support tickets for your tenants."
                            icon="HeadphonesIcon"
                            color="gray"
                            onClick={() => setActivePage('support')}
                            delay={0.9}
                        />
                    )}
                </div>
            </div>
        </div>
        </>
    );
};

export default AdminDashboardPage;